import { useState, useEffect } from "react";
import { authFetch } from "../lib/authFetch";

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

export function PushNotificationsManager() {
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (typeof Notification === "undefined") return;
    setPermission(Notification.permission);
    if (localStorage.getItem("portail-push-dismissed") === "1") setDismissed(true);
    if (localStorage.getItem("portail-push-subscribed") === "1") setSubscribed(true);
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") { setLoading(false); return; }

      const { publicKey } = await authFetch("/api/push/vapid-public-key").then((r) => r.json());
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      const subJson = subscription.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
      await authFetch("/api/portail/push/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
          deviceLabel: navigator.userAgent.slice(0, 100),
        }),
      });
      setSubscribed(true);
      localStorage.setItem("portail-push-subscribed", "1");
    } catch (e) {
      console.error("[PushManager portail] subscribe failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem("portail-push-dismissed", "1");
  };

  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  if (permission === "granted" || permission === "denied") return null;
  if (dismissed || subscribed || !permission) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100vw-2rem)] max-w-sm bg-card border border-border rounded-xl shadow-xl p-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
        <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">Activer les notifications</p>
        <p className="text-xs text-muted-foreground mt-0.5">Recevez une alerte à chaque nouveau message</p>
        <div className="flex gap-2 mt-3">
          <button
            onClick={handleEnable}
            disabled={loading}
            className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {loading ? "…" : "Activer"}
          </button>
          <button
            onClick={handleDismiss}
            className="px-3 py-1.5 text-muted-foreground rounded-lg text-xs hover:text-foreground hover:bg-secondary transition-colors"
          >
            Plus tard
          </button>
        </div>
      </div>
      <button onClick={handleDismiss} className="p-1 text-muted-foreground hover:text-foreground transition-colors shrink-0">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </div>
  );
}
